import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Loader2, Plus, Trash2, AlertTriangle, Wrench } from "lucide-react";
import { toast } from "sonner";
import { SortHeaderButton } from "@/components/ui/sort-header-button";
import { useTableControls } from "@/lib/use-table-controls";

export type RuleDto = {
    id: string;
    item_id: string | null;
    item_code: string | null;
    item_name: string | null;
    category_id: string | null;
    category_path: string | null;
    tag: string | null;
    override_unit_price: string | null;
    discount_pct_off_list: string | null;
    markup_pct_on_cost: string | null;
    override_hourly_rate: string | null;
    override_test_fee: string | null;
    priority: number;
    notes: string | null;
    effective_from: string | null;
    effective_to: string | null;
    is_active: boolean;
    created_at: string;
};

type LookupItem = { id: string; code: string; name: string; kind: string };
type LookupCategory = { id: string; path: string; name: string };

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
    return (
        <div>
            <Label>{label}</Label>
            {children}
            {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
        </div>
    );
}

export function CustomerRules({
    customerId,
    rules,
    defaultCollapsed = true,
}: {
    customerId: string;
    rules: RuleDto[];
    defaultCollapsed?: boolean;
}) {
    const qc = useQueryClient();
    const [collapsed, setCollapsed] = useState(defaultCollapsed);
    const [showForm, setShowForm] = useState(false);
    const [search, setSearch] = useState("");
    const [pickedItem, setPickedItem] = useState<LookupItem | null>(null);
    const [pickedCategory, setPickedCategory] = useState<LookupCategory | null>(null);
    const [tag, setTag] = useState("");
    const [scope, setScope] = useState<"item" | "category" | "tag">("item");
    const [overrideUnit, setOverrideUnit] = useState("");
    const [discountPct, setDiscountPct] = useState("");
    const [markupPct, setMarkupPct] = useState("");
    const [hourlyRate, setHourlyRate] = useState("");
    const [testFee, setTestFee] = useState("");
    const [notes, setNotes] = useState("");

    const itemSearch = useQuery({
        queryKey: ["pricebook-item-search", search],
        queryFn: () =>
            api.get<{ items: LookupItem[] }>(`/api/pricebook/items?q=${encodeURIComponent(search)}&limit=20`),
        enabled: scope === "item" && search.length >= 2,
    });

    const categorySearch = useQuery({
        queryKey: ["pricebook-category-search"],
        queryFn: () => api.get<{ categories: LookupCategory[] }>(`/api/pricebook/categories`),
        enabled: scope === "category" && showForm,
    });

    const filteredCategories = useMemo(() => {
        const all = categorySearch.data?.categories ?? [];
        const q = search.trim().toLowerCase();
        if (!q) return all.slice(0, 30);
        return all.filter((c) => c.path.toLowerCase().includes(q) || c.name.toLowerCase().includes(q)).slice(0, 30);
    }, [categorySearch.data?.categories, search]);

    const {
        visible: sortedRules,
        sortKey,
        sortDir,
        toggleSort,
    } = useTableControls(rules, {
        sortId: "rate-card.customer-rules",
        sortAccessors: { scope: (r) => (r.item_id ? r.item_code : r.category_id ? r.category_path : r.tag) },
    });

    const createRule = useMutation({
        mutationFn: async () => {
            const body: Record<string, unknown> = { priority: 100, notes: notes || null };
            if (scope === "item") {
                if (!pickedItem) throw new Error("Pick a pricebook item first.");
                body.itemId = pickedItem.id;
            } else if (scope === "category") {
                if (!pickedCategory) throw new Error("Pick a pricebook category first.");
                body.categoryId = pickedCategory.id;
            } else {
                if (!tag.trim()) throw new Error("Tag is required.");
                body.tag = tag.trim().toLowerCase();
            }
            if (overrideUnit) body.overrideUnitPrice = Number(overrideUnit);
            if (discountPct) body.discountPctOffList = Number(discountPct);
            if (markupPct) body.markupPctOnCost = Number(markupPct);
            if (hourlyRate) body.overrideHourlyRate = Number(hourlyRate);
            if (testFee) body.overrideTestFee = Number(testFee);
            if (
                !body.overrideUnitPrice &&
                !body.discountPctOffList &&
                !body.markupPctOnCost &&
                !body.overrideHourlyRate &&
                !body.overrideTestFee
            ) {
                throw new Error("Set at least one pricing effect.");
            }
            await api.post(`/api/customers/${customerId}/pricing/rules`, body);
        },
        onSuccess: () => {
            toast.success("Rule added");
            qc.invalidateQueries({ queryKey: ["customer-pricing", customerId] });
            setShowForm(false);
            setPickedItem(null);
            setPickedCategory(null);
            setTag("");
            setOverrideUnit("");
            setDiscountPct("");
            setMarkupPct("");
            setHourlyRate("");
            setTestFee("");
            setNotes("");
        },
        onError: (e: any) => toast.error(`Add failed: ${e?.body?.error ?? e?.message ?? "unknown"}`),
    });

    const deleteRule = useMutation({
        mutationFn: async (ruleId: string) => api.delete(`/api/customers/${customerId}/pricing/rules/${ruleId}`),
        onSuccess: () => {
            toast.success("Rule removed");
            qc.invalidateQueries({ queryKey: ["customer-pricing", customerId] });
        },
    });

    return (
        <Card className="border-border/60">
            <CardHeader className="pb-3">
                <button
                    type="button"
                    className="flex w-full items-center gap-2 text-left"
                    onClick={() => setCollapsed((v) => !v)}
                >
                    <CardTitle className="text-sm flex items-center gap-2 flex-1">
                        <Wrench className="h-4 w-4" /> Exceptions (advanced)
                        <Badge variant="secondary" className="ml-1">
                            {rules.length}
                        </Badge>
                    </CardTitle>
                    <span className="text-xs text-muted-foreground">{collapsed ? "Show" : "Hide"}</span>
                </button>
            </CardHeader>
            {!collapsed && (
                <CardContent>
                    <p className="text-xs text-muted-foreground mb-3">
                        Item, category, or tag overrides. Most contracts don&apos;t need these — use the sections above
                        first.
                    </p>
                    <div className="flex justify-end mb-3">
                        <Button size="sm" variant="outline" onClick={() => setShowForm((v) => !v)}>
                            <Plus className="h-3.5 w-3.5 mr-1" /> {showForm ? "Cancel" : "Add rule"}
                        </Button>
                    </div>
                    {showForm && (
                        <div className="rounded-md border p-4 mb-4 space-y-3 bg-muted/30">
                            <div className="grid gap-3 md:grid-cols-2">
                                <div>
                                    <Label>Scope</Label>
                                    <Select
                                        value={scope}
                                        onChange={(e) => {
                                            setScope(e.target.value as "item" | "category" | "tag");
                                            setSearch("");
                                            setPickedItem(null);
                                            setPickedCategory(null);
                                        }}
                                    >
                                        <option value="item">Specific pricebook item</option>
                                        <option value="category">Pricebook category</option>
                                        <option value="tag">Match by tag</option>
                                    </Select>
                                </div>
                                {scope === "item" ? (
                                    <div>
                                        <Label>Find item</Label>
                                        <Input
                                            placeholder="Type code or name…"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                        />
                                        {pickedItem && (
                                            <div className="mt-1 text-xs flex items-center gap-2">
                                                <Badge>{pickedItem.code}</Badge>
                                                <span className="text-muted-foreground">{pickedItem.name}</span>
                                            </div>
                                        )}
                                        {!pickedItem && itemSearch.data?.items && itemSearch.data.items.length > 0 && (
                                            <div className="border mt-1 rounded-md max-h-40 overflow-auto bg-background">
                                                {itemSearch.data.items.slice(0, 10).map((it) => (
                                                    <button
                                                        key={it.id}
                                                        type="button"
                                                        className="w-full text-left px-2 py-1 text-xs hover:bg-muted/50 flex items-center gap-2"
                                                        onClick={() => {
                                                            setPickedItem(it);
                                                            setSearch("");
                                                        }}
                                                    >
                                                        <Badge variant="outline" className="text-[10px]">
                                                            {it.kind}
                                                        </Badge>
                                                        <span className="font-mono">{it.code}</span>
                                                        <span className="text-muted-foreground truncate">
                                                            {it.name}
                                                        </span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : scope === "category" ? (
                                    <div>
                                        <Label>Find category</Label>
                                        <Input
                                            placeholder="Type category path…"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                        />
                                        {pickedCategory && (
                                            <div className="mt-1 text-xs text-muted-foreground">
                                                {pickedCategory.path}
                                            </div>
                                        )}
                                        {!pickedCategory && filteredCategories.length > 0 && (
                                            <div className="border mt-1 rounded-md max-h-40 overflow-auto bg-background">
                                                {filteredCategories.map((cat) => (
                                                    <button
                                                        key={cat.id}
                                                        type="button"
                                                        className="w-full text-left px-2 py-1 text-xs hover:bg-muted/50 truncate"
                                                        onClick={() => {
                                                            setPickedCategory(cat);
                                                            setSearch("");
                                                        }}
                                                    >
                                                        {cat.path}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div>
                                        <Label>Tag</Label>
                                        <Input
                                            placeholder="e.g. test, repair"
                                            value={tag}
                                            onChange={(e) => setTag(e.target.value)}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 pt-2 border-t">
                                <Field label="Flat unit price ($)">
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={overrideUnit}
                                        onChange={(e) => setOverrideUnit(e.target.value)}
                                    />
                                </Field>
                                <Field label="% off list">
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={discountPct}
                                        onChange={(e) => setDiscountPct(e.target.value)}
                                    />
                                </Field>
                                <Field label="Cost + markup %">
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={markupPct}
                                        onChange={(e) => setMarkupPct(e.target.value)}
                                    />
                                </Field>
                                <Field label="Override hourly rate">
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={hourlyRate}
                                        onChange={(e) => setHourlyRate(e.target.value)}
                                    />
                                </Field>
                                <Field label="Override test fee">
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={testFee}
                                        onChange={(e) => setTestFee(e.target.value)}
                                    />
                                </Field>
                            </div>
                            <Field label="Notes (optional)">
                                <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
                            </Field>
                            <div className="flex justify-end">
                                <Button onClick={() => createRule.mutate()} disabled={createRule.isPending}>
                                    {createRule.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add rule"}
                                </Button>
                            </div>
                        </div>
                    )}
                    {rules.length === 0 ? (
                        <div className="text-sm text-muted-foreground flex items-center gap-2 py-2">
                            <AlertTriangle className="h-4 w-4" /> No exceptions — standard pricebook + settings above
                            apply.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-xs text-muted-foreground border-b">
                                    <tr>
                                        <th className="text-left py-2 font-medium">
                                            <SortHeaderButton
                                                label="Scope"
                                                active={sortKey === "scope"}
                                                dir={sortDir}
                                                onClick={() => toggleSort("scope")}
                                            />
                                        </th>
                                        <th className="text-left py-2 font-medium">Effect</th>
                                        <th className="text-right py-2 font-medium" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedRules.map((r) => (
                                        <tr key={r.id} className="border-b last:border-0">
                                            <td className="py-2 align-top text-xs">
                                                {r.item_id ? (
                                                    <span className="font-mono">{r.item_code}</span>
                                                ) : r.category_id ? (
                                                    r.category_path
                                                ) : (
                                                    `tag: ${r.tag}`
                                                )}
                                            </td>
                                            <td className="py-2 align-top text-xs">{describeRule(r)}</td>
                                            <td className="py-2 align-top text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => deleteRule.mutate(r.id)}
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            )}
        </Card>
    );
}

function describeRule(r: RuleDto): string {
    const parts: string[] = [];
    if (r.override_unit_price != null) parts.push(`Flat $${Number(r.override_unit_price).toFixed(2)}`);
    if (r.discount_pct_off_list != null) parts.push(`-${Number(r.discount_pct_off_list)}% off list`);
    if (r.markup_pct_on_cost != null) parts.push(`Cost + ${Number(r.markup_pct_on_cost)}%`);
    if (r.override_hourly_rate != null) parts.push(`$${Number(r.override_hourly_rate).toFixed(2)}/hr`);
    if (r.override_test_fee != null) parts.push(`Test $${Number(r.override_test_fee).toFixed(2)}`);
    return parts.length ? parts.join(" · ") : "(no effect)";
}
